import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Shield, 
  Check, 
  X,
  AlertTriangle,
  AlertOctagon,
  Clock,
  Play,
  Trash2,
  RefreshCw,
  Copy,
  CheckCircle,
  XCircle,
  Info,
  Lock,
  User,
  Zap,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const riskColors = {
  low: { bg: "bg-green-500/10", border: "border-green-500/30", text: "text-green-400", glow: "glow-green" },
  medium: { bg: "bg-yellow-500/10", border: "border-yellow-500/30", text: "text-yellow-400", glow: "glow-yellow" },
  high: { bg: "bg-orange-500/10", border: "border-orange-500/30", text: "text-orange-400", glow: "" },
  critical: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", glow: "glow-red" }
};

const statusColors = {
  pending: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400" },
  approved: { bg: "bg-green-500/10", border: "border-green-500/30", text: "text-green-400" },
  rejected: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400" },
  executed: { bg: "bg-purple-500/10", border: "border-purple-500/30", text: "text-purple-400" }
};

const RiskIcon = ({ level }) => {
  switch (level) {
    case "critical":
      return <AlertOctagon className="w-5 h-5 text-red-400" />;
    case "high":
      return <AlertTriangle className="w-5 h-5 text-orange-400" />;
    case "medium":
      return <Info className="w-5 h-5 text-yellow-400" />;
    default:
      return <CheckCircle className="w-5 h-5 text-green-400" />;
  }
};

export default function Approvals() {
  const { user, canApproveRisk } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [copiedId, setCopiedId] = useState(null);
  const [batchExecuting, setBatchExecuting] = useState(false);

  const fetchApprovals = async (status = null) => {
    try {
      const url = status ? `${API}/approvals?status=${status}` : `${API}/approvals`;
      const response = await axios.get(url);
      setApprovals(response.data);
    } catch (error) {
      toast.error("Failed to fetch approvals");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/approvals/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  useEffect(() => {
    fetchApprovals(activeTab === "all" ? null : activeTab);
    fetchStats();
  }, [activeTab]);

  const handleApprove = async (approvalId) => {
    try {
      await axios.put(`${API}/approvals/${approvalId}`, { action: "approve" });
      toast.success("Command approved");
      fetchApprovals(activeTab === "all" ? null : activeTab);
      fetchStats();
    } catch (error) {
      toast.error("Failed to approve command");
    }
  };

  const handleReject = async (approvalId) => {
    try {
      await axios.put(`${API}/approvals/${approvalId}`, { action: "reject" });
      toast.success("Command rejected");
      fetchApprovals(activeTab === "all" ? null : activeTab);
      fetchStats();
    } catch (error) {
      toast.error("Failed to reject command");
    }
  };

  const handleExecute = async (approvalId) => {
    try {
      const response = await axios.post(`${API}/approvals/${approvalId}/execute`);
      const result = response.data;
      
      if (result.ssh_output && result.ssh_output.exit_code === 0) {
        toast.success("Command executed successfully via SSH");
      } else if (result.ssh_output && result.ssh_output.error) {
        toast.warning("Command executed with errors — check output");
      } else if (result.execution_result?.includes("simulated")) {
        toast.info("No SSH configured — execution was simulated");
      } else {
        toast.success("Command executed");
      }
      
      // Refresh to show results
      fetchApprovals(activeTab === "all" ? null : activeTab);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute command");
    }
  };

  const handleDelete = async (approvalId) => {
    if (window.confirm("Delete this approval request?")) {
      try {
        await axios.delete(`${API}/approvals/${approvalId}`);
        toast.success("Approval deleted");
        fetchApprovals(activeTab === "all" ? null : activeTab);
        fetchStats();
      } catch (error) {
        toast.error("Failed to delete approval");
      }
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Command copied");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleBatchExecute = async () => {
    const approvedIds = approvals.filter(a => a.status === "approved").map(a => a.id);
    if (approvedIds.length === 0) {
      toast.info("No approved commands to execute");
      return;
    }
    if (!window.confirm(`Execute ${approvedIds.length} approved command(s)? This cannot be undone.`)) return;
    
    setBatchExecuting(true);
    try {
      const res = await axios.post(`${API}/approvals/batch-execute`, approvedIds);
      const data = res.data;
      toast.success(`Batch complete: ${data.succeeded}/${data.total} succeeded`);
      if (data.results?.some(r => !r.success)) {
        const failed = data.results.filter(r => !r.success);
        failed.forEach(f => toast.error(`Failed: ${f.error || 'Unknown error'}`));
      }
      fetchApprovals(activeTab === "all" ? null : activeTab);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Batch execution failed");
    } finally {
      setBatchExecuting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="p-6 space-y-6" data-testid="approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">Command Approvals</h1>
          <p className="text-zinc-400 text-sm mt-1">Review and approve commands before execution</p>
        </div>
        <div className="flex items-center gap-3">
          {stats?.approved > 0 && (
            <Button
              onClick={handleBatchExecute}
              disabled={batchExecuting}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="batch-execute-btn"
            >
              {batchExecuting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              {batchExecuting ? "Executing..." : `Execute All Approved (${stats.approved})`}
            </Button>
          )}
          <Button
            onClick={() => {
              fetchApprovals(activeTab === "all" ? null : activeTab);
              fetchStats();
            }}
            variant="outline"
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            data-testid="refresh-approvals-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500">Pending</p>
              <p className="text-2xl font-bold font-mono text-blue-400">{stats?.pending || 0}</p>
            </div>
            <Clock className="w-6 h-6 text-blue-400/50" />
          </CardContent>
        </Card>
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500">Approved</p>
              <p className="text-2xl font-bold font-mono text-green-400">{stats?.approved || 0}</p>
            </div>
            <CheckCircle className="w-6 h-6 text-green-400/50" />
          </CardContent>
        </Card>
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500">Rejected</p>
              <p className="text-2xl font-bold font-mono text-red-400">{stats?.rejected || 0}</p>
            </div>
            <XCircle className="w-6 h-6 text-red-400/50" />
          </CardContent>
        </Card>
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500">Executed</p>
              <p className="text-2xl font-bold font-mono text-purple-400">{stats?.executed || 0}</p>
            </div>
            <Play className="w-6 h-6 text-purple-400/50" />
          </CardContent>
        </Card>
        <Card className={`bg-[#0c0c0e] border-white/5 ${stats?.critical_pending > 0 ? 'border-red-500/50' : ''}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-zinc-500">Critical Pending</p>
              <p className="text-2xl font-bold font-mono text-red-400">{stats?.critical_pending || 0}</p>
            </div>
            <AlertOctagon className="w-6 h-6 text-red-400/50" />
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-black/30 border border-white/10">
          <TabsTrigger value="pending" className="data-[state=active]:bg-blue-600" data-testid="tab-pending">
            Pending {stats?.pending > 0 && <Badge variant="secondary" className="ml-2 bg-blue-500/20 text-blue-400">{stats.pending}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-blue-600" data-testid="tab-approved">
            Approved
          </TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-blue-600" data-testid="tab-rejected">
            Rejected
          </TabsTrigger>
          <TabsTrigger value="executed" className="data-[state=active]:bg-blue-600" data-testid="tab-executed">
            Executed
          </TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-blue-600" data-testid="tab-all">
            All
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : approvals.length === 0 ? (
            <Card className="bg-[#0c0c0e] border-white/5">
              <CardContent className="py-12 text-center">
                <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No {activeTab === "all" ? "" : activeTab} approval requests</p>
                <p className="text-zinc-500 text-sm mt-1">Commands awaiting review will appear here</p>
              </CardContent>
            </Card>
          ) : (
            approvals.map((approval) => (
              <Card 
                key={approval.id}
                className={`bg-[#0c0c0e] border-white/5 ${
                  approval.risk_level === 'critical' ? 'border-l-4 border-l-red-500' :
                  approval.risk_level === 'high' ? 'border-l-4 border-l-orange-500' :
                  approval.risk_level === 'medium' ? 'border-l-4 border-l-yellow-500' :
                  'border-l-4 border-l-green-500'
                }`}
                data-testid={`approval-${approval.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Header with badges */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <RiskIcon level={approval.risk_level} />
                        <Badge className={`${riskColors[approval.risk_level].bg} ${riskColors[approval.risk_level].border} ${riskColors[approval.risk_level].text} border`}>
                          {approval.risk_level.toUpperCase()}
                        </Badge>
                        <Badge className={`${statusColors[approval.status].bg} ${statusColors[approval.status].border} ${statusColors[approval.status].text} border`}>
                          {approval.status}
                        </Badge>
                        <Badge variant="outline" className="text-zinc-400 border-zinc-600">
                          {approval.os_type}
                        </Badge>
                      </div>

                      {/* Description */}
                      <p className="text-sm text-zinc-300 mb-2">{approval.description}</p>

                      {/* Command */}
                      <div className="relative group">
                        <code className="block text-sm text-green-400 font-mono bg-black/40 p-3 rounded-lg border border-white/5 overflow-x-auto">
                          {approval.command}
                        </code>
                        <button
                          onClick={() => copyToClipboard(approval.command, approval.id)}
                          className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          {copiedId === approval.id ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3 text-zinc-400" />
                          )}
                        </button>
                      </div>

                      {/* Warning */}
                      {approval.ai_warning && (
                        <div className={`mt-2 p-2 rounded-lg text-sm flex items-start gap-2 ${
                          approval.risk_level === 'critical' ? 'bg-red-500/10 text-red-300' :
                          approval.risk_level === 'high' ? 'bg-orange-500/10 text-orange-300' :
                          'bg-yellow-500/10 text-yellow-300'
                        }`}>
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {approval.ai_warning}
                        </div>
                      )}

                      {/* Timestamps */}
                      <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                        <span>Created: {formatDate(approval.created_at)}</span>
                        {approval.reviewed_at && <span>Reviewed: {formatDate(approval.reviewed_at)}</span>}
                        {approval.executed_at && <span>Executed: {formatDate(approval.executed_at)}</span>}
                      </div>

                      {/* Reviewer info */}
                      {approval.reviewed_by_email && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
                          <User className="w-3 h-3" />
                          <span>Reviewed by: {approval.reviewed_by_email} ({approval.reviewed_by_role})</span>
                        </div>
                      )}

                      {/* Executor info */}
                      {approval.executed_by_email && (
                        <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
                          <User className="w-3 h-3" />
                          <span>Executed by: {approval.executed_by_email}</span>
                        </div>
                      )}

                      {/* Execution result */}
                      {approval.execution_result && (
                        <div className="mt-3 rounded-lg border border-purple-500/20 overflow-hidden" data-testid={`exec-result-${approval.id}`}>
                          <div className="px-3 py-1.5 bg-purple-500/10 flex items-center gap-2 border-b border-purple-500/20">
                            <Play className="w-3.5 h-3.5 text-purple-400" />
                            <span className="text-xs font-medium text-purple-400">Execution Output</span>
                            {approval.ssh_output?.exit_code !== undefined && (
                              <Badge className={`text-[10px] ml-auto ${
                                approval.ssh_output.exit_code === 0 
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                  : 'bg-red-500/20 text-red-400 border-red-500/30'
                              }`}>
                                Exit: {approval.ssh_output.exit_code}
                              </Badge>
                            )}
                          </div>
                          <pre className="px-3 py-3 bg-black/60 text-xs text-zinc-300 font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                            {approval.execution_result}
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      {approval.status === "pending" && (
                        <>
                          {canApproveRisk(approval.risk_level) ? (
                            <Button
                              size="sm"
                              onClick={() => handleApprove(approval.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-${approval.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              disabled
                              className="bg-zinc-700 text-zinc-400 cursor-not-allowed"
                              title={`Your role cannot approve ${approval.risk_level} risk commands`}
                              data-testid={`approve-disabled-${approval.id}`}
                            >
                              <Lock className="w-4 h-4 mr-1" />
                              No Access
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(approval.id)}
                            className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-400"
                            data-testid={`reject-${approval.id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {approval.status === "approved" && (
                        <Button
                          size="sm"
                          onClick={() => handleExecute(approval.id)}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid={`execute-${approval.id}`}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Execute
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(approval.id)}
                        className="hover:bg-red-500/20 text-zinc-400 hover:text-red-400"
                        data-testid={`delete-${approval.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
